import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Printer, ArrowRight, Truck, User, Package, Calendar, Banknote, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';

export function DrDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dr, setDr] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDrDetail();
  }, [id]);

  const fetchDrDetail = async () => {
    try {
      const response = await api.get(`/api/drs/${id}`);
      const drData = response.data.data || response.data;
      console.log('DR Data:', drData);
      console.log('Shipments:', drData.shipments || drData.items || []);
      setDr(drData);
    } catch (error) {
      console.error('Error fetching DR details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getStatusColor = (status: string) => {
    const s = (status || '').toLowerCase();
    if (s.includes('delivered')) return 'bg-green-100 text-green-800 border-green-200';
    if (s.includes('return')) return 'bg-red-100 text-red-800 border-red-200';
    if (s.includes('out')) return 'bg-blue-100 text-blue-800 border-blue-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (!dr) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-500 mb-4">لم يتم العثور على بيانات التسليم</p>
          <Button onClick={() => navigate('/drs')}>العودة للقائمة</Button>
        </div>
      </div>
    );
  }

  const shipments = dr.shipments || dr.items || [];

  const printStyles = `
    @media print {
      .no-print { display: none !important; }
      body { background: white !important; margin: 0; padding: 0; }
      .shadow-lg { box-shadow: none !important; }
      .rounded-lg { border-radius: 0 !important; }
      @page { margin: 1cm; size: A4; }
    }
  `;

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8" dir="rtl">
      <div className="max-w-5xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden print:shadow-none print:max-w-none">
        
        {/* Header */}
        <div className="border-b-2 border-gray-200 p-6 bg-white print:p-4">
          <div className="flex items-center justify-between">
            <div className="text-right">
              <h1 className="text-3xl font-bold text-gray-900 mb-1">Target Express</h1>
              <p className="text-sm text-gray-600 font-semibold">بيانات تسليم مندوب (DR)</p>
              <p className="text-xs text-gray-500 mt-1">Shipping, Logistics & Fulfillment Solutions</p>
            </div>
            <div className="flex items-center gap-4">
              <img src="/logo-waigh-transparent.png" alt="Target Express" className="h-28 w-auto object-contain" />
            </div>
          </div>
        </div>

        {/* DR Info */}
        <div className="p-6 bg-gray-50 border-b border-gray-200 print:bg-white print:p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Package className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">رقم إيصال التسليم</p>
                <p className="text-lg font-bold text-gray-900 font-mono">{dr.drsNumber || `DR-${id}`}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">اسم المندوب</p>
                <p className="text-lg font-bold text-gray-900">{dr.delegateName || 'غير محدد'}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Calendar className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">تاريخ الإصدار</p>
                <p className="text-lg font-bold text-gray-900">
                  {dr.createdAt ? new Date(dr.createdAt).toLocaleDateString('ar-EG') : new Date().toLocaleDateString('ar-EG')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* معلومات إضافية للمندوب */}
        <div className="p-6 bg-gradient-to-r from-orange-50 to-blue-50 border-y border-gray-200 print:bg-white print:p-4">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-orange-600" />
            معلومات الشحنة للمندوب
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-lg border-2 border-orange-200 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Banknote className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">صافي البوليصه (COD)</p>
                  <p className="text-2xl font-bold text-orange-600">{dr.codAmount || '0.00'} ج.م</p>
                </div>
              </div>
              <p className="text-xs text-gray-500">المبلغ المستحق التحصيل من العميل</p>
            </div>

            <div className="bg-white p-4 rounded-lg border-2 border-blue-200 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-600 mb-2">محتوى الشحنة</p>
                  <p className="text-sm font-semibold text-gray-900 leading-relaxed">
                    {dr.shipmentContents || 'لا توجد تفاصيل'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Shipments Table */}
        <div className="p-6 print:p-4">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Truck className="w-5 h-5 text-orange-600" />
            تفاصيل الشحنة المسندة
          </h2>
          
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 border-b border-gray-300">
                <tr>
                  <th className="text-right p-3 font-bold text-gray-900 w-12">#</th>
                  <th className="text-right p-3 font-bold text-gray-900">رقم الشحنة</th>
                  <th className="text-right p-3 font-bold text-gray-900">المرسل إليه</th>
                  <th className="text-right p-3 font-bold text-gray-900">العنوان</th>
                  <th className="text-right p-3 font-bold text-gray-900">الهاتف</th>
                  <th className="text-right p-3 font-bold text-gray-900">المدينة</th>
                  <th className="text-right p-3 font-bold text-gray-900">الحالة</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50 border-b border-gray-100 bg-gradient-to-r from-orange-50/50 to-blue-50/50">
                  <td className="p-3 font-bold text-gray-600 text-center bg-orange-100 rounded-r-lg">1</td>
                  <td className="p-3 font-mono font-bold text-orange-700 text-sm font-semibold">
                    {dr.shipmentNumber || '-'}
                  </td>
                  <td className="p-3 font-semibold text-gray-900">
                    {dr.recipientName || '-'}
                  </td>
                  <td className="p-3 text-gray-700 text-xs max-w-xs truncate" title={dr.shipmentAddress}>
                    {dr.shipmentAddress || 'لا يوجد عنوان'}
                  </td>
                  <td className="p-3 text-gray-700 text-sm dir-ltr text-right font-mono">
                    {dr.recipientPhone || '-'}
                  </td>
                  <td className="p-3 text-gray-700 font-semibold">
                    {dr.shipmentCity || 'غير محدد'}
                  </td>
                  <td className="p-3 bg-gray-100 rounded-l-lg">
                    <Badge className={`${getStatusColor(dr.status)} text-xs px-3 py-1 font-semibold`}>
                      {dr.status || 'قيد المعالجة'}
                    </Badge>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer & Signatures */}
        <div className="p-6 bg-gray-50 border-t-2 border-gray-200 print:bg-white print:p-4">
          <div className="grid grid-cols-2 gap-8 mb-6">
            <div className="text-center">
              <p className="text-sm font-bold text-gray-700 mb-8">توقيع المندوب</p>
              <div className="border-b border-gray-400 w-3/4 mx-auto"></div>
              <p className="text-xs text-gray-500 mt-2">الاسم: {dr.delegateName || '........................'}</p>
            </div>
            <div className="text-center">
              <p className="text-sm font-bold text-gray-700 mb-8">توقيع مدير المستودع</p>
              <div className="border-b border-gray-400 w-3/4 mx-auto"></div>
              <p className="text-xs text-gray-500 mt-2">الختم والتوقيع</p>
            </div>
          </div>

          <div className="flex justify-between items-center text-xs text-gray-500 border-t border-gray-300 pt-4">
            <div>
              <p className="font-semibold text-gray-700">Target Express</p>
              <p>شحن ولوجستيات</p>
            </div>
            <div className="text-center">
              <p>تم إنشاء هذا المستند إلكترونياً</p>
              <p>تاريخ الطباعة: {new Date().toLocaleString('ar-EG')}</p>
            </div>
            <div className="text-left">
              <p>© 2026 Target Express</p>
              <p>جميع الحقوق محفوظة</p>
            </div>
          </div>
        </div>

      </div>

      {/* Action Buttons (Hidden in Print) */}
      <div className="max-w-5xl mx-auto mt-6 flex justify-center gap-4 no-print">
        <Button onClick={handlePrint} className="bg-orange-600 hover:bg-orange-700 px-8 py-6 text-lg">
          <Printer className="w-5 h-5 ml-2" /> طباعة الإيصال
        </Button>
        <Button onClick={() => navigate('/drs')} variant="outline" className="px-8 py-6 text-lg border-gray-300">
          <ArrowRight className="w-5 h-5 ml-2" /> العودة للقائمة
        </Button>
      </div>

      {/* Print Styles */}
      <style>{printStyles}</style>
    
      <style>{`@media print {
  .no-print, nav, aside, button, .fixed, .sticky { display: none !important; }
  body { background: white !important; margin: 0 !important; padding: 0 !important; }
  .max-w-5xl { max-width: 100% !important; margin: 0 !important; padding: 2px !important; }
  h1 { font-size: 9pt !important; }
  h2, h3 { font-size: 7pt !important; }
  .text-lg { font-size: 7pt !important; }
  .text-2xl { font-size: 9pt !important; }
  table { font-size: 6pt !important; }
  table th, table td { padding: 1px 2px !important; line-height: 1.0 !important; }
  .p-6 { padding: 2px !important; }
  .p-4 { padding: 2px !important; }
  .mb-4 { margin-bottom: 2px !important; }
  .mb-6 { margin-bottom: 3px !important; }
  .gap-6 { gap: 4px !important; }
  .grid-cols-1 { grid-template-columns: repeat(2, 1fr) !important; gap: 3px !important; }
  .w-10 { width: 20px !important; height: 20px !important; }
  .w-5 { width: 10px !important; height: 10px !important; }
  .h-24 { height: 35px !important; }
  @page { margin: 3mm; size: A5; }
}`}</style>
    </div>
  );
}
